import { cn } from '@forest/shared'
import { createMemo, For, on, onCleanup, type Accessor } from 'solid-js'
import type { ClassicContentFilter } from '~/lib/classic-content-filter'
import { sortClassicProducts, type ClassicSort } from '~/lib/classic-sorting'
import { createWithinReach } from '~/lib/within-reach'
import type { CatalogueContent, CatalogueProduct } from '~/services/catalogue'
import type { CatalogueGridSlot } from '~/store/catalogue-grid'
import { CATALOGUE_GRID_CLASS, CatalogueSlot } from './catalogue-card'

export function ClassicCatalogue(props: {
  // The Classic content filter (GLOSSARY.md): the one kind on screen.
  content: ClassicContentFilter
  isCelebrating?: (content: CatalogueContent, gid: number) => boolean
  // A free product unlocked this session that has jumped to its owned place
  // (GLOSSARY.md "Classic collection order").
  isInOwnedPlace?: (content: CatalogueContent, gid: number) => boolean
  isOwned?: (content: CatalogueContent, gid: number) => boolean
  onSelect: (product: CatalogueProduct) => void
  // Reports a slot the first time it comes within reach of the screen
  // (GLOSSARY.md "In-view hydration").
  onSlotWithinReach?: (slot: CatalogueGridSlot) => void
  serverNow: Accessor<number>
  slots: CatalogueGridSlot[]
  sort: ClassicSort
}) {
  const withinReach = createWithinReach()
  // Price sorts read the effective price as of the last sort or filter
  // change, deliberately untracking the clock: a sale flipping mid-view
  // updates the pill, never the order (ADR 0024's stable-slot rule outranks
  // live re-sorting).
  const sortedAtMs = createMemo(
    on([() => props.sort, () => props.content], () => props.serverNow()),
  )
  // Content and sort keys are candidate data, so this re-runs on a filter or
  // sort change, and otherwise only when an unlocked free product jumps to
  // its owned place (ADR 0043) — slot fills and collapses never reorder the
  // grid, and the jump moves that card alone.
  const slots = createMemo(() => {
    const isInOwnedPlace = props.isInOwnedPlace
    return sortClassicProducts(
      props.slots.filter((slot) => slot.content === props.content),
      props.sort,
      sortedAtMs(),
      (slot) =>
        slot.owned || (isInOwnedPlace?.(slot.content, slot.gid) ?? false),
    )
  })

  return (
    <ul
      data-testid="classic-catalogue"
      class={cn(CATALOGUE_GRID_CLASS, 'pb-[72px]')}
    >
      <For each={slots()}>
        {(slot) => (
          <CatalogueSlot
            isCelebrating={props.isCelebrating}
            isOwned={props.isOwned}
            onSelect={props.onSelect}
            ref={(element) => {
              const onReach = props.onSlotWithinReach
              if (!onReach) return
              onCleanup(withinReach.observe(element, () => onReach(slot)))
            }}
            serverNow={props.serverNow}
            slot={slot}
          />
        )}
      </For>
    </ul>
  )
}
